import { useEffect, useState } from 'react';
import { motion, HTMLMotionProps } from 'framer-motion';

interface Props extends HTMLMotionProps<"img"> {
  src: string;
}

export const TransparentLogo = ({ src, alt, ...props }: Props) => {
  const [processedSrc, setProcessedSrc] = useState<string>("");

  useEffect(() => {
    const img = new Image();
    img.src = src;
    img.onload = () => {
      const canvas = document.createElement("canvas");
      canvas.width = img.width;
      canvas.height = img.height;
      const ctx = canvas.getContext("2d");
      if (!ctx) return;
      
      ctx.drawImage(img, 0, 0);
      const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
      const data = imageData.data;
      
      // Iterate through all pixels and make dark greys transparent
      for (let i = 0; i < data.length; i += 4) {
        const r = data[i];
        const g = data[i + 1];
        const b = data[i + 2];
        
        // Find the brightest color channel for this pixel
        const maxVal = Math.max(r, g, b);
        
        // The background is dark grey, the gold is bright.
        // We completely delete pixels with a brightness under 45.
        if (maxVal < 45) {
          data[i + 3] = 0; // Alpha to 0 (completely transparent)
        } 
        // We lightly feather pixels between 45 and 65 for incredibly smooth anti-aliased edges!
        else if (maxVal < 65) {
          data[i + 3] = ((maxVal - 45) / 20) * 255;
        }
      }
      
      ctx.putImageData(imageData, 0, 0);
      setProcessedSrc(canvas.toDataURL("image/png"));
    };
  }, [src]);

  // Show nothing until it finishes making the background transparent (takes ~2 milliseconds)
  if (!processedSrc) return null;

  return <motion.img src={processedSrc} alt={alt} {...props} />;
};
